import { Link } from "react-router-dom";
import { MapPin, ArrowRight } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";
import { IMG } from "@/data/site";
import { Reveal, Eyebrow } from "@/components/Reveal";
import Seo from "@/components/Seo";

const PLACE_IMGS = [IMG.church, "/images/podbrdo.jpg", "/images/krizevac.jpg", IMG.house];

export default function Medjugorje() {
  const { t, lang } = useLang();
  const m = t.medjugorjePage;

  return (
    <div data-testid="medjugorje-page" key={lang} className="pt-16 sm:pt-20">
      <Seo title={t.seo.medjugorje.title} description={t.seo.medjugorje.desc} image="/images/st-james-church.jpg" />

      <header className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={IMG.church} alt="Stone church in Međugorje" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-[#1F2421]/60" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 py-28 sm:py-36">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.28em] font-medium text-[#D8CFBC] mb-4">Bosna i Hercegovina</p>
            <h1 className="font-serif-display text-5xl sm:text-6xl lg:text-7xl text-[#F9F8F3]">{m.title}</h1>
            <p className="text-lg sm:text-xl text-[#F9F8F3]/85 font-light leading-relaxed mt-6 max-w-2xl">{m.lead}</p>
          </Reveal>
        </div>
      </header>

      <section className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 py-16 sm:py-24">
        <Reveal>
          <p className="text-lg sm:text-xl text-[#4A504B] font-light leading-relaxed max-w-3xl">{m.intro}</p>
        </Reveal>
      </section>

      <section data-testid="medjugorje-places" className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 pb-20">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
          {m.places.map((p, i) => (
            <Reveal key={p.name} delay={i * 0.07}>
              <article
                data-testid={`place-card-${i}`}
                className="group bg-white rounded-2xl overflow-hidden border border-[#1F2421]/8 hover:shadow-[0_20px_50px_-20px_rgba(31,36,33,0.25)] transition-shadow duration-500"
              >
                <div className="overflow-hidden aspect-[16/9]">
                  <img
                    src={PLACE_IMGS[i]}
                    alt={p.name}
                    loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
                  />
                </div>
                <div className="p-6 sm:p-8">
                  <span className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] text-[#4A5D3E] font-semibold">
                    <MapPin size={12} /> {p.dist}
                  </span>
                  <h2 className="font-serif-display text-2xl sm:text-3xl text-[#1F2421] mt-3">{p.name}</h2>
                  <p className="text-sm sm:text-base text-[#4A504B] leading-relaxed mt-3">{p.desc}</p>
                </div>
              </article>
            </Reveal>
          ))}
        </div>
      </section>

      <section data-testid="medjugorje-when" className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 pb-16">
        <Reveal>
          <h2 className="font-serif-display text-3xl sm:text-4xl text-[#1F2421]">{m.when.title}</h2>
          <p className="text-base sm:text-lg text-[#4A504B] leading-relaxed mt-5 max-w-3xl">{m.when.text}</p>
        </Reveal>
      </section>

      <section data-testid="medjugorje-stay" className="bg-[#F3EFE6] py-16 sm:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
          <Reveal>
            <h2 className="font-serif-display text-3xl sm:text-4xl text-[#1F2421]">{m.stay.title}</h2>
            <p className="text-base sm:text-lg text-[#4A504B] leading-relaxed mt-5 max-w-3xl">{m.stay.text}</p>
            <div className="flex flex-wrap gap-3 mt-8">
              <Link to="/sobe" data-testid="medj-link-rooms" className="inline-flex items-center rounded-full bg-[#4A5D3E] hover:bg-[#3A4A31] text-[#F9F8F3] text-sm font-semibold px-5 py-2.5 transition-colors">
                {m.stay.rooms}
              </Link>
              <Link to="/lokacija" data-testid="medj-link-location" className="inline-flex items-center rounded-full border border-[#4A5D3E]/40 text-[#4A5D3E] text-sm font-semibold px-5 py-2.5 hover:bg-[#E8EFE6] transition-colors">
                {m.stay.location}
              </Link>
              <Link to="/kontakt" data-testid="medj-link-contact" className="inline-flex items-center rounded-full border border-[#1F2421]/15 text-[#1F2421] text-sm font-semibold px-5 py-2.5 hover:bg-white transition-colors">
                {m.stay.contact}
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-[#F3EFE6] py-16 sm:py-20 border-t border-[#1F2421]/8">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 flex flex-col md:flex-row md:items-center justify-between gap-8">
          <Reveal>
            <Eyebrow>Sobe Bevanda</Eyebrow>
            <p className="text-lg sm:text-xl text-[#1F2421] font-light max-w-xl">{m.note}</p>
          </Reveal>
          <Reveal delay={0.12}>
            <Link
              to="/kontakt"
              data-testid="medjugorje-inquiry-cta"
              className="group inline-flex items-center gap-2 rounded-full bg-[#4A5D3E] hover:bg-[#3A4A31] text-[#F9F8F3] text-sm font-semibold px-7 py-3.5 transition-colors duration-300"
            >
              {t.cta.sendInquiry}
              <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
